import type { FieldDefinition, Filter, InventoryRow } from "@estelle/domain";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { useDataSource } from "@/adapters/data-source";
import { LoadFailed, NoMatches } from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { DataTable } from "@/components/table/DataTable";
import { Pagination } from "@/components/table/Pagination";
import { useCursorHistory } from "@/components/table/useCursorHistory";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SELECTED_CHOICE } from "@/components/list/selectedChoice";
import { cn } from "@/lib/utils";
import {
  selectWarehouse,
  warehouseSelection,
} from "@/modules/inventory/byWarehouse";
import {
  InventoryMobileCard,
  ProductCell,
} from "@/modules/inventory/screens/InventoryRowParts";
import {
  AdjustableFigure,
  Figure,
  InTransitCell,
  UnavailableCell,
} from "@/modules/inventory/screens/StockCells";
import { ThresholdControl } from "@/modules/inventory/screens/ThresholdEditor";
import { projectInventoryRow } from "@/modules/inventory/projections";
import type { SectionScreenProps } from "@/modules/types";

/**
 * The cells that are more than a number (#379, #521, #602): the product with
 * its photo, Unavailable with its breakdown, Available and On hand with a quick
 * adjust, In transit saying whose figure it is, and the threshold, which a
 * person who may change it can.
 */
function makeRenderOverride(listing: "levels" | "low-stock") {
  return (field: FieldDefinition, row: InventoryRow) => {
    switch (field.name) {
      case "productTitle":
        return <ProductCell row={row} />;
      case "unavailable":
        return <UnavailableCell row={row} />;
      case "committed":
        return <Figure value={row.committed ?? 0} />;
      case "available":
        return <AdjustableFigure row={row} figure="available" />;
      case "onHand":
        return <AdjustableFigure row={row} figure="onHand" />;
      case "inTransit":
        return <InTransitCell row={row} />;
      case "lowStockThreshold":
        return <ThresholdControl row={row} listing={listing} />;
      default:
        return undefined;
    }
  };
}

/** Where the last warehouse a person looked at is remembered, in this browser. */
const LAST_WAREHOUSE_KEY = "estelle.stockLevels.warehouse";
const ALL = "*";

function remembered(): string | null {
  try {
    return localStorage.getItem(LAST_WAREHOUSE_KEY);
  } catch {
    return null;
  }
}
function remember(code: string) {
  try {
    localStorage.setItem(LAST_WAREHOUSE_KEY, code);
  } catch {
    // A browser that will not store it just opens on the first warehouse.
  }
}

/**
 * Stock Levels (FR-INV-1): one warehouse at a time, with what Shopify's own
 * inventory screen shows for a location — Unavailable, Committed, Available,
 * On hand — and the ERP's In transit beside them (#602 part C).
 *
 * The warehouse picker is a shortcut to the filter the builder would write for
 * "Warehouse is X" (`byWarehouse.ts`), so the choice lives in the URL, the
 * filter chip and saved views like any other condition. With none in the URL it
 * opens on the last warehouse chosen here, else the first active one; "All
 * warehouses" is still a choice, and the Warehouse column shows which is which.
 */
export function StockLevels(props: SectionScreenProps) {
  return (
    <div className="flex h-full min-h-0 flex-col">
      <WarehouseBar
        filter={props.filter}
        onChangeFilter={props.onChangeFilter}
      />
      <LowStockBar
        filter={props.filter}
        onChangeFilter={props.onChangeFilter}
      />
      <div className="flex min-h-0 flex-1 flex-col">
        <StockLevelsTable {...props} />
      </div>
    </div>
  );
}

function WarehouseBar({
  filter,
  onChangeFilter,
}: Pick<SectionScreenProps, "filter" | "onChangeFilter">) {
  const data = useDataSource();
  const warehouses = useQuery({
    queryKey: ["inventory", "stock-levels", "warehouses"],
    queryFn: async () =>
      (await data.warehouses.list({ limit: 100 })).items.filter(
        (warehouse) => warehouse.isActive,
      ),
  });
  const selection = warehouseSelection(filter);
  const opened = useRef(false);

  // With no warehouse in the URL, open on the last one chosen, else the first.
  useEffect(() => {
    if (opened.current || !warehouses.data) return;
    opened.current = true;
    if (selection.kind !== "all") return;
    const last = remembered();
    if (last === ALL) return;
    const target =
      warehouses.data.find((warehouse) => warehouse.code === last) ??
      warehouses.data[0];
    if (target) onChangeFilter(selectWarehouse(filter, target.code));
  }, [warehouses.data, selection.kind, filter, onChangeFilter]);

  const value =
    selection.kind === "one"
      ? selection.code
      : selection.kind === "all"
        ? ALL
        : "custom";

  return (
    <div className="flex flex-wrap items-center gap-2 border-b px-4 py-2">
      <span className="text-muted-foreground text-sm">Warehouse</span>
      <Select
        value={value}
        onValueChange={(next) => {
          remember(next);
          onChangeFilter(selectWarehouse(filter, next === ALL ? null : next));
        }}
      >
        <SelectTrigger aria-label="Warehouse" className="h-9 w-56 rounded-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>All warehouses</SelectItem>
          {(warehouses.data ?? []).map((warehouse) => (
            <SelectItem key={warehouse.id} value={warehouse.code}>
              {warehouse.name} ({warehouse.code})
            </SelectItem>
          ))}
          {selection.kind === "custom" ? (
            <SelectItem value="custom" disabled>
              Custom filter
            </SelectItem>
          ) : null}
        </SelectContent>
      </Select>
    </div>
  );
}

/** The one condition the chip adds and removes: the server's own "low" rule. */
const isLowStockCondition = (child: Filter["children"][number]) =>
  child.kind === "condition" &&
  child.field === "belowThreshold" &&
  child.operator === "is_true";

/**
 * The Low stock chip (#602 part B), where Low Stock used to be a page: a toggle
 * that adds or removes `belowThreshold is true` through the shared filter, so it
 * ANDs with whatever else is chosen, lives in the URL and shows in the filter
 * bar like any condition. The count and the threshold in force come from the
 * same rule the server filters by, so the words and the rows agree.
 */
function LowStockBar({
  filter,
  onChangeFilter,
}: Pick<SectionScreenProps, "filter" | "onChangeFilter">) {
  const data = useDataSource();
  const low = useQuery({
    queryKey: ["inventory", "low-stock-count"],
    queryFn: () => data.inventory.listLowStock({ limit: 1 }),
  });
  const active = filter.children.some(isLowStockCondition);
  const count = low.data?.total ?? null;

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b px-4 py-2 text-sm">
      <button
        type="button"
        aria-pressed={active}
        onClick={() => {
          onChangeFilter({
            ...filter,
            children: active
              ? filter.children.filter((child) => !isLowStockCondition(child))
              : [
                  ...filter.children,
                  {
                    kind: "condition",
                    field: "belowThreshold",
                    operator: "is_true",
                  },
                ],
          });
        }}
        className={cn(
          "inline-flex min-h-9 items-center gap-1.5 rounded-full border px-3 text-sm font-medium",
          active ? SELECTED_CHOICE : "hover:bg-accent",
        )}
      >
        Low stock
      </button>
      {low.data ? (
        <span className="text-muted-foreground text-xs">
          {count === null
            ? ""
            : `${String(count)} level${count === 1 ? "" : "s"}, `}
          threshold {low.data.defaultThreshold} unless a level has its own
        </span>
      ) : null}
    </div>
  );
}

/** The table: a row per variant at the chosen warehouse (or per warehouse, for All). */
function StockLevelsTable({
  fields,
  filter,
  search,
  sort,
  onToggleSort,
  onClearFilter,
}: SectionScreenProps) {
  const data = useDataSource();
  // With the Low stock filter on, a higher or lower threshold can make a row leave
  // the list, and the save says so (the toast the old Low Stock page gave).
  const listing = filter.children.some(isLowStockCondition)
    ? "low-stock"
    : "levels";

  const [limit, setLimit] = useState(25);
  const [history, setHistory] = useCursorHistory();
  const cursor = history[history.length - 1];

  const query = useQuery({
    queryKey: ["inventory", "levels", filter, search, sort, limit, cursor],
    queryFn: () => data.inventory.list({ filter, search, sort, limit, cursor }),
  });

  if (query.isPending) return <TableSkeleton />;

  if (query.isError) {
    return (
      <LoadFailed
        message={query.error.message}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <DataTable
        fields={fields}
        rows={query.data.items}
        getRowId={(row) => row.id}
        project={projectInventoryRow}
        sort={sort}
        onToggleSort={onToggleSort}
        renderOverride={makeRenderOverride(listing)}
        mobileRow={(row) => (
          <InventoryMobileCard
            row={row}
            threshold={
              <ThresholdControl
                row={row}
                listing={listing}
                className="-ml-1 px-1.5"
              />
            }
          />
        )}
        emptyState={<NoMatches onClear={onClearFilter} />}
        footer={
          <Pagination
            page={query.data}
            limit={limit}
            onLimitChange={(next) => {
              setLimit(next);
              setHistory([undefined]);
            }}
            history={history}
            onNavigate={setHistory}
          />
        }
      />
    </div>
  );
}
